/**
 * SectionHeader (S1.4) — shared title row for the detail sections:
 * localized title (+ optional subtitle) and an optional chunk-Edit button.
 *
 * EDIT AFFORDANCE (Melissa 2026-08-28, app-wide ruling: "that purple one is
 * correct"): every Edit button in the app is the SAME control — antd
 * `type="primary"` (the purple `colorPrimary` fill), an `EditOutlined` icon,
 * and the 44px `controlHeightXL` token. This component is the highest-
 * leverage seat for that rule: 23 detail sections render their Edit through
 * it, so the pattern lives here rather than being retyped per section.
 */

import { Button, Typography, theme } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { controlHeightTokens } from '@/styles/tokens';

const { Title, Text } = Typography;

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  /** Localized edit button label (button renders only when onEdit is set). */
  editLabel?: string;
  onEdit?: () => void;
  editDisabled?: boolean;
  /** Optional extra action node (e.g. Add vendor button). */
  extra?: React.ReactNode;
}

export const SectionHeader = ({
  title,
  subtitle,
  editLabel,
  onEdit,
  editDisabled = false,
  extra,
}: SectionHeaderProps) => {
  const { token } = theme.useToken();

  return (
    <div className="tw-flex tw-justify-between tw-items-start tw-gap-4 tw-mb-4 tw-flex-wrap">
      <div>
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
        {subtitle && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {subtitle}
          </Text>
        )}
      </div>
      <div className="tw-flex tw-gap-2">
        {extra}
        {onEdit && editLabel && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={onEdit}
            disabled={editDisabled}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {editLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default SectionHeader;
